import { useMemo, useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Select } from 'antd'
import { TrashIcon, PlusIcon, BanIcon, CheckCircleIcon } from '@heroicons/react/outline'

import { Button, makeRowId } from '../../../../components'
import { FieldLabel } from '../../../Configurations/Coberturas/createCoverages/formPrimitives'
import { bulkUpdateShippingCosts, fetchShippingCostsUpdateStatus, fetchTarifZonesStatus, fetchLastmileXdocks, syncZoneCosts } from '../../../../services'
import { CoverageJobProgressPanel } from '../../../Configurations/Coberturas/CoverageJobProgressPanel'
import { SHIPPING_COSTS_JOB_SCOPE } from '../../../Configurations/Coberturas/CoverageJobProgressView'
import { useBulkCostJob } from './useBulkCostJob'
import { CostValidationResult } from './CostValidationResult'
import { costIsPositiveInCents, currencyFor, isSpecialZone, tierRangeFrom, weightSortKey, zoneBoundXdockId } from './costHelpers'

const T_PREFIX = 'lastmile_management.shipping_costs.single_form'
const RANGE_T_PREFIX = 'lastmile_management.shipping_costs.edit_zone' // reuse the ranges-table labels
// Stable empty reference: react-query returns `undefined` until data loads; a `= []`
// default would mint a NEW array every render, and any effect depending on it would
// re-run forever (setRows → re-render → new array → re-run). See the preload effect.
const EMPTY_XDOCKS = []

export const fmtWeight = w => {
  if (w == null) return '' // guard null/undefined → never render the literal "undefined"
  const n = Number(w)
  if (!Number.isFinite(n)) return ''
  return Number.isInteger(n) ? String(n) : String(w)
}
// Design labels tiers as a weight RANGE: first tier "≤ X kg", the rest "prev – X kg".
export const tierRangeLabel = (prevMax, max) => (prevMax == null ? `≤ ${fmtWeight(max)} kg` : `${fmtWeight(prevMax)} – ${fmtWeight(max)} kg`)

/**
 * Create OR update a SINGLE shipping cost, mirroring the coverage "create one /
 * update one" form. Submits through the same async bulk endpoints (one item), so
 * it reuses the job progress panel. `mode` = "create" | "update".
 */
export const SingleCostForm = ({ shippingMethodId, country, mode, initialZone, onClose, t }) => {
  const isCreate = mode === 'create'
  const queryClient = useQueryClient()
  const job = useBulkCostJob(bulkUpdateShippingCosts) // update-mode async job

  // Create fields: pick a zone + cross-dock and add one or more weight ranges.
  const [zone, setZone] = useState(initialZone || '')
  const [xdockId, setXdockId] = useState(null)
  const [rows, setRows] = useState([])
  // Update fields
  const [costId, setCostId] = useState(null)
  const [newCost, setNewCost] = useState('')

  const setRow = (i, key, val) => setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)))
  // A new range is always the open-ended TOP band, so it only makes sense once the current last
  // range has a ceiling — otherwise you'd stack two tiers with no upper bound. Gate the button on it.
  const canAddRange = rows.length === 0 || String(rows[rows.length - 1]?.max ?? '').trim() !== ''
  const addRow = () => {
    if (!canAddRange) return
    setRows(rs => [...rs, { __rid: makeRowId(), max: '', cost: '', enabled: true }])
  }
  const removeRow = i => setRows(rs => rs.filter((_, idx) => idx !== i))
  // Existing (id-bearing) tiers are disabled, not deleted: PUT /shipping-costs/zone is a
  // REPLACE, so trashing a preloaded tier would permanently destroy that priced band.
  const toggleEnabled = i => setRows(rs => rs.map((r, idx) => (idx === i ? { ...r, enabled: r.enabled === false } : r)))

  const { data: statusData } = useQuery(['tarif_zones_status', shippingMethodId, '', false], () => fetchTarifZonesStatus(shippingMethodId), {
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
  const methodIsCubbo = !!statusData?.is_cubbo

  const { data: xdocksData } = useQuery(['lastmile-xdocks', country], () => fetchLastmileXdocks({ country, includeZones: true }), {
    enabled: isCreate && methodIsCubbo && Boolean(country),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
  const xdocks = xdocksData || EMPTY_XDOCKS
  const xdockOptions = useMemo(() => xdocks.map(x => ({ label: x.name, value: x.id })), [xdocks])

  // Create-mode: when a zone is picked, preload its already-configured tiers (with ids)
  // ONCE so it acts as create-or-edit for that zone. A ref guard (keyed by zone) keeps a
  // background refetch / sibling invalidateQueries from clobbering in-progress edits, and
  // drops `xdocks` (the binding is resolved in the effect below) so the array reference
  // churning never re-runs the setRows loop.
  const seededZoneRef = useRef(null)
  useEffect(() => {
    if (!isCreate) return
    if (!zone) {
      seededZoneRef.current = null
      setRows([])
      return
    }
    if (seededZoneRef.current === zone) return
    if (!statusData) return // wait for the status data before seeding (and marking) the zone
    seededZoneRef.current = zone
    const z = (statusData.zones || []).find(zz => zz.zone === zone)
    const tiers = [...(z?.tiers || [])].sort((a, b) => weightSortKey(a.max_compound_weight) - weightSortKey(b.max_compound_weight))
    setRows(
      tiers.map(ti => ({
        __rid: makeRowId(),
        id: ti.shipping_cost_id,
        max: String(ti.max_compound_weight ?? ''),
        cost: String(ti.cost ?? ''),
        enabled: ti.coverage_enabled !== false,
      })),
    )
  }, [isCreate, zone, statusData])

  // Resolve the zone's current cross-dock once the xdocks load. Sets a PRIMITIVE, so
  // React's Object.is bailout stops it from looping when the xdocks reference changes.
  useEffect(() => {
    if (!isCreate || !zone) {
      setXdockId(null)
      return
    }
    setXdockId(zoneBoundXdockId(xdocks, zone))
  }, [isCreate, zone, xdocks])

  const syncMutation = useMutation(syncZoneCosts, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tarif_zones_status'])
      queryClient.invalidateQueries(['lastmile-xdocks'])
      onClose?.()
    },
  })

  // Update-mode: every existing tier as a pickable option, labeled "ZONA · range".
  // Zones sorted numerically (ZONA_9 before ZONA_20), tiers by weight so the range
  // ("≤ 5 kg", "5 – 10 kg") reads correctly. Keep each tier's cost for "Costo actual".
  const tierOptions = useMemo(() => {
    const zones = [...(statusData?.zones || [])].sort((a, b) => String(a.zone).localeCompare(String(b.zone), undefined, { numeric: true }))
    const opts = []
    zones.forEach(z => {
      const tiers = [...(z.tiers || [])]
        .filter(ti => ti.shipping_cost_id != null)
        .sort((a, b) => weightSortKey(a.max_compound_weight) - weightSortKey(b.max_compound_weight))
      tiers.forEach((tier, idx) => {
        const prevMax = idx === 0 ? null : tiers[idx - 1].max_compound_weight
        opts.push({
          value: tier.shipping_cost_id,
          label: `${z.zone} · ${tierRangeLabel(prevMax, tier.max_compound_weight)}`,
          cost: tier.cost,
        })
      })
    })
    return opts
  }, [statusData])

  const selectedTier = useMemo(() => tierOptions.find(o => o.value === costId) || null, [tierOptions, costId])
  const currentCost = selectedTier?.cost != null ? Number(selectedTier.cost).toFixed(2) : ''
  const displayCurrency = currencyFor(country)

  // Create-mode: a cost can only be created for a zone the method covers, so offer only
  // zones declared in shipping_coverages (numeric order: ZONA_9 before ZONA_20).
  const zoneOptions = useMemo(
    () =>
      [...(statusData?.zones || [])]
        .filter(z => z.has_coverage)
        .map(z => z.zone)
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true })),
    [statusData],
  )

  // The DESDE bound of each tier is derived from the sorted ceilings of the ENABLED rows
  // (a disabled tier isn't runtime-selectable, so it shouldn't shift a neighbor's band), so
  // an out-of-order row still reads as a correct range instead of "20 – 3 kg".
  const rangeFrom = max => tierRangeFrom(rows, max)

  // Validation gates (create). ALL tiers (enabled or disabled) must carry a positive
  // ceiling: sync_zone requires it for every tier, and a blank ceiling on a disabled
  // legacy (NULL) tier would otherwise be silently rewritten to 0 and can collide on the
  // unique (zone, weight) index. Enabled tiers additionally need cost > 0. Ceilings must
  // be distinct across the zone (same as the bulk wizards' duplicate_tier). xdock is
  // required for Cubbo methods (the banner sends the operator here precisely for it).
  const enabledRows = rows.filter(r => r.enabled !== false)
  const allMaxes = rows.map(r => Number(r.max))
  const hasDuplicateMax = new Set(allMaxes).size !== allMaxes.length
  const tiersComplete = rows.every(r => Number(r.max) > 0 && Number.isFinite(Number(r.cost)))
  const enabledCostsPositive = enabledRows.every(r => costIsPositiveInCents(r.cost))
  // Special zones (NONE_APPLIES / EXTENDED_ZONE) are exempt from the xdock requirement.
  const missingXdock = isCreate && methodIsCubbo && !isSpecialZone(zone) && !xdockId
  const canSave = isCreate
    ? Boolean(zone.trim() && enabledRows.length > 0 && tiersComplete && enabledCostsPositive && !hasDuplicateMax && !missingXdock && !syncMutation.isLoading)
    : costId != null && costIsPositiveInCents(newCost)

  // Inline reason shown next to a disabled Save, so the operator knows what to fix.
  // Check "incomplete" before "duplicate": two blank ceilings are Number('')===0, which
  // also trips hasDuplicateMax — but the real problem is the empty fields, not a collision.
  const saveHint = missingXdock
    ? t(`${T_PREFIX}.hint_xdock`)
    : !tiersComplete
      ? t(`${RANGE_T_PREFIX}.incomplete_hint`)
      : hasDuplicateMax
        ? t(`${RANGE_T_PREFIX}.hint_duplicate_weight`)
        : t(`${RANGE_T_PREFIX}.incomplete_hint`)

  const handleSave = () => {
    if (isCreate) {
      // Upsert the whole zone (create new tiers + update existing by id + reassign xdock)
      // so preloaded tiers don't collide on the unique index. Send tiers weight-sorted.
      syncMutation.mutate({
        shipping_method_id: Number(shippingMethodId),
        tarif_zone: zone.trim(),
        currency_code: displayCurrency || undefined,
        ...(methodIsCubbo && xdockId ? { xdock_id: xdockId } : {}),
        tiers: [...rows]
          .sort((a, b) => weightSortKey(a.max) - weightSortKey(b.max))
          .map(r => ({
            ...(r.id ? { id: r.id } : {}),
            max_compound_weight: Number(r.max),
            cost: Number(r.cost),
            coverage_enabled: r.enabled !== false,
          })),
      })
      return
    }
    job.commit({ items: [{ id: Number(costId), cost: Number(newCost) }] })
  }

  if (!isCreate && job.jobId) {
    return (
      <CoverageJobProgressPanel
        jobId={job.jobId}
        statusFetcher={fetchShippingCostsUpdateStatus}
        scope={SHIPPING_COSTS_JOB_SCOPE}
        onCompleted={() => queryClient.invalidateQueries(['tarif_zones_status'])}
        onClose={() => {
          job.reset()
          onClose?.()
        }}
      />
    )
  }

  // Match the system's form field style (see SingleCoverageForm INPUT_CLS).
  const field = 'block w-full rounded-md border-gray-300 shadow-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm'

  return (
    <div className="space-y-4">
      {isCreate ? (
        <>
          <label className="block">
            <FieldLabel required>{t(`${T_PREFIX}.zone`)}</FieldLabel>
            <select className={field} value={zone} onChange={e => setZone(e.target.value)}>
              <option value="">{t(`${T_PREFIX}.zone_select_placeholder`)}</option>
              {zoneOptions.map(z => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </label>

          {methodIsCubbo && (
            <label className="block">
              <FieldLabel required>{t(`${T_PREFIX}.xdock`)}</FieldLabel>
              <select className={field} value={xdockId ?? ''} onChange={e => setXdockId(e.target.value ? Number(e.target.value) : null)}>
                <option value="">{t(`${T_PREFIX}.xdock_placeholder`)}</option>
                {xdockOptions.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div>
            <p className="mb-2 text-sm font-semibold text-gray-900">{t(`${RANGE_T_PREFIX}.ranges_title`)}</p>
            <div className="overflow-hidden rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${RANGE_T_PREFIX}.col_from`)}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${RANGE_T_PREFIX}.col_to`)}</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">{t(`${RANGE_T_PREFIX}.col_cost`)}</th>
                    <th className="w-12 px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {rows.map((r, i) => {
                    const disabled = r.enabled === false
                    const from = rangeFrom(r.max)
                    return (
                      <tr key={r.__rid} className={disabled ? 'bg-gray-50 opacity-60' : ''}>
                        <td className={`px-4 py-2 text-gray-600 ${disabled ? 'line-through' : ''}`}>
                          {from == null ? t(`${RANGE_T_PREFIX}.from_zero`) : t(`${RANGE_T_PREFIX}.from_more`, { weight: from })}
                        </td>
                        <td className="px-4 py-2">
                          <div className="relative w-40">
                            {/* The ceiling stays editable even when the tier is disabled: a legacy tier
                                seeded with a NULL/blank ceiling could otherwise never be corrected, blocking
                                every save for the zone (sync_zone requires a ceiling on every tier). */}
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className={`${field} pr-9`}
                              value={r.max}
                              placeholder={t(`${RANGE_T_PREFIX}.to_placeholder`)}
                              onChange={e => setRow(i, 'max', e.target.value)}
                            />
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 sm:text-sm">kg</span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="relative w-40">
                            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 sm:text-sm">
                              {displayCurrency}
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className={`${field} pl-12`}
                              value={r.cost}
                              disabled={disabled}
                              placeholder="0.00"
                              onChange={e => setRow(i, 'cost', e.target.value)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-2 text-center">
                          {r.id ? (
                            <button
                              type="button"
                              onClick={() => toggleEnabled(i)}
                              className="p-1 text-gray-400 transition-colors hover:text-cubbo-700"
                              title={disabled ? t(`${RANGE_T_PREFIX}.enable`) : t(`${RANGE_T_PREFIX}.disable`)}
                            >
                              {disabled ? <CheckCircleIcon className="h-5 w-5" /> : <BanIcon className="h-5 w-5" />}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => removeRow(i)}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title={t(`${RANGE_T_PREFIX}.remove`)}
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <button
              type="button"
              onClick={addRow}
              disabled={!canAddRange}
              title={canAddRange ? undefined : t(`${RANGE_T_PREFIX}.add_range_disabled_hint`)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-dashed border-gray-300 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <PlusIcon className="h-4 w-4" />
              {t(`${RANGE_T_PREFIX}.add_range`)}
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="block">
            <FieldLabel required>{t(`${T_PREFIX}.tier`)}</FieldLabel>
            {/* Searchable: a Cubbo method can have hundreds of zone·tier options, so the operator
                types to filter (by zone or range) instead of scrolling a native select. */}
            <Select
              showSearch
              allowClear
              className="block w-full"
              value={costId ?? undefined}
              onChange={v => setCostId(v ?? null)}
              options={tierOptions.map(o => ({ value: o.value, label: o.label }))}
              placeholder={t(`${T_PREFIX}.tier_placeholder`)}
              optionFilterProp="label"
            />
          </label>

          {selectedTier && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <FieldLabel>{t(`${T_PREFIX}.current_cost`)}</FieldLabel>
                <div className="flex rounded-md shadow-sm">
                  {displayCurrency && (
                    <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                      {displayCurrency}
                    </span>
                  )}
                  <input
                    type="text"
                    disabled
                    value={currentCost}
                    className={`block w-full ${displayCurrency ? 'rounded-none rounded-r-md' : 'rounded-md'} border-gray-300 bg-gray-50 text-gray-500 shadow-sm sm:text-sm`}
                  />
                </div>
              </div>
              <div>
                <FieldLabel required>{t(`${T_PREFIX}.new_cost`)}</FieldLabel>
                <div className="flex rounded-md shadow-sm">
                  {displayCurrency && (
                    <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-100 px-3 text-gray-500 sm:text-sm">
                      {displayCurrency}
                    </span>
                  )}
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={newCost}
                    onChange={e => setNewCost(e.target.value)}
                    className={`block w-full ${displayCurrency ? 'rounded-none rounded-r-md' : 'rounded-md'} border-gray-300 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm`}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {isCreate ? (
        syncMutation.isError && <p className="text-sm text-red-600">{syncMutation.error?.response?.data?.error || t(`${T_PREFIX}.required`)}</p>
      ) : (
        <CostValidationResult validation={job.validation} error={job.error} t={t} />
      )}

      <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4">
        {isCreate && !canSave && !syncMutation.isLoading && rows.length > 0 && <p className="text-xs text-gray-400">{saveHint}</p>}
        <Button type="primary" disabled={!canSave || (!isCreate && job.status === 'committing')} onClick={handleSave}>
          {(isCreate ? syncMutation.isLoading : job.status === 'committing') ? t(`${T_PREFIX}.saving`) : t(`${T_PREFIX}.save`)}
        </Button>
      </div>
    </div>
  )
}
